import { Options } from '../options.interface';
import { BANKING_PROCESSOR_API_CONFIG } from './banking-processor-api.config';
import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BankingBankAccount, BankingBankInfo, BankingOffer, BankingPartner, BankingUser, UserEligibility } from '@wizbii/utils/models';
import { Observable } from 'rxjs';

@Injectable()
export class BankingUserWebservice {
  readonly #http = inject(HttpClient);
  readonly #bankingProcessorApiConfig = inject(BANKING_PROCESSOR_API_CONFIG);

  readonly #baseUrl = this.#bankingProcessorApiConfig.baseUrl;

  readonly get = (userId: string, options?: Options): Observable<BankingUser> =>
    this.#http.get<BankingUser>(`${this.#baseUrl}/v1/user/${userId}`, options);

  readonly update = (
    userId: string,
    bankingUser: { userId: string } & Partial<BankingUser>,
    options?: Options
  ): Observable<BankingUser> => this.#http.put<BankingUser>(`${this.#baseUrl}/v1/user/${userId}`, bankingUser, options);

  readonly checkUserEligibility = (userId: string, options?: Options): Observable<boolean> =>
    this.#http.get<boolean>(`${this.#baseUrl}/v1/user/${userId}/check-eligibility`, options);

  readonly checkUserEligibilityV2 = (userId: string, options?: Options): Observable<UserEligibility> =>
    this.#http.get<UserEligibility>(`${this.#baseUrl}/v2/user/${userId}/check-eligibility`, options);

  readonly onboarding = (
    userId: string,
    redirectUrl: string,
    language = 'fr',
    options?: Options
  ): Observable<BankingUser> =>
    this.#http.post<BankingUser>(`${this.#baseUrl}/v1/user/${userId}/onboarding`, { language, redirectUrl }, options);

  readonly getBankAccount = (userId: string, options?: Options): Observable<BankingBankAccount> =>
    this.#http.get<BankingBankAccount>(`${this.#baseUrl}/v1/user/${userId}/bankAccount`, options);

  readonly bankAccount = (userId: string, iban: string, options?: Options): Observable<BankingBankAccount> =>
    this.#http.post<BankingBankAccount>(`${this.#baseUrl}/v1/user/${userId}/bankAccount`, { iban }, options);

  readonly getBankRedirection = (options?: Options): Observable<any> =>
    this.#http.get<any>(`${this.#baseUrl}/v1/bankRedirection`, options);

  readonly verifiyIbanMatchPartner = (userId: string, iban: string, options?: Options): Observable<BankingBankInfo> =>
    this.#http.post<any>(`${this.#baseUrl}/v1/user/${userId}/bankAccount/validate`, { iban }, options);

  readonly completeAlreadyClientOnboarding = (userId: string, options?: Options): Observable<BankingUser> =>
    this.#http.post<BankingUser>(`${this.#baseUrl}/v1/user/${userId}/already-client`, options);

  readonly getSingleOffer = (trackId: string, options?: Options): Observable<BankingOffer> =>
    this.#http.get<BankingOffer>(`${this.#baseUrl}/v1/offer/${trackId}`, options);

  readonly getSingleOfferByUser = (userId: string, options?: Options): Observable<BankingOffer> =>
    this.#http.get<BankingOffer>(`${this.#baseUrl}/v1/user/${userId}/offer`, options);

  readonly getOffers = (userId: string, options?: Options): Observable<BankingOffer[]> =>
    this.#http.get<BankingOffer[]>(`${this.#baseUrl}/v1/user/${userId}/offers`, options);

  readonly getStepQrCode = (userId: string, path: string, options?: Options): Observable<{ dataUri: string }> =>
    this.#http.get<{ dataUri: string }>(`${this.#baseUrl}/v2/user/${userId}/step/qrcode.png?path=${path}`, {
      ...options,
    });

  readonly getQrCode = (userId: string, options?: Options): Observable<{ dataUri: string }> =>
    this.#http.get<{ dataUri: string }>(`${this.#baseUrl}/v1/user/${userId}/qrcode.png`, { ...options });

  readonly resetOnboarding = (userId: string, body?: any, options?: Options): Observable<BankingUser> =>
    this.#http.post<BankingUser>(`${this.#baseUrl}/v1/user/${userId}/reset-onboarding`, body ?? {}, options);
}
